import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useCpPortal } from '../../hooks/useCpPortal';
import logoIcon from '../../assets/logo-icon.png';
import { ClientPartnerBrandBar } from '../client-portal/ClientPartnerBrandBar';
import {
  LayoutDashboard,
  ArrowRightLeft,
  Upload,
  LogOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Building2,
  Wallet,
  Users,
  Mail,
  FileText,
  Key,
  BookOpen,
  BarChart3,
  Receipt,
} from 'lucide-react';

interface NavItem {
  label: string;
  suffix: string;
  icon: React.ElementType;
  group: string;
  adminOnly?: boolean;
  makerOrAdminOnly?: boolean;
  mirrorHidden?: boolean;
  revenueShareOnly?: boolean;
  passthroughOnly?: boolean;
  mobile?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', suffix: '/dashboard', icon: LayoutDashboard, group: 'Overview', mobile: true },
  { label: 'Disbursement Runs', suffix: '/runs', icon: ArrowRightLeft, group: 'Payouts', mobile: true },
  { label: 'Upload Beneficiaries', suffix: '/upload', icon: Upload, group: 'Payouts', mobile: true },
  { label: 'Registered users', suffix: '/users', icon: Users, group: 'Insights', mobile: true },
  { label: 'Reports', suffix: '/reports', icon: BarChart3, group: 'Insights' },
  { label: 'Finance', suffix: '/finance', icon: Receipt, group: 'Insights', adminOnly: true },
  { label: 'Welcome email', suffix: '/welcome-email', icon: Mail, group: 'Partner', makerOrAdminOnly: true },
  { label: 'Team', suffix: '/team', icon: Users, group: 'Partner', adminOnly: true },
  { label: 'Credentials', suffix: '/credentials', icon: Key, group: 'Partner', mirrorHidden: true, passthroughOnly: true },
  { label: 'API docs', suffix: '/api-docs', icon: BookOpen, group: 'Partner', mirrorHidden: true, passthroughOnly: true },
  { label: 'Agreements', suffix: '/agreements', icon: FileText, group: 'Partner', mirrorHidden: true },
];

function getPageTitle(pathname: string, basePath: string): string {
  const relative = pathname.startsWith(basePath) ? pathname.slice(basePath.length) : pathname;
  const rules: { test: (p: string) => boolean; title: string }[] = [
    { test: (p) => p.startsWith('/agreements'), title: 'Agreement Center' },
    { test: (p) => p.startsWith('/credentials'), title: 'Credentials Center' },
    { test: (p) => p.startsWith('/api-docs'), title: 'Partner API Documentation' },
    { test: (p) => p.startsWith('/welcome-email'), title: 'Welcome email' },
    { test: (p) => p.startsWith('/users'), title: 'Registered users' },
    { test: (p) => p.startsWith('/reports'), title: 'Transaction Reports' },
    { test: (p) => p.startsWith('/finance') || p.startsWith('/statements'), title: 'Finance' },
    { test: (p) => p.startsWith('/upload'), title: 'Upload Beneficiaries' },
    { test: (p) => p.startsWith('/team'), title: 'Team' },
    { test: (p) => /^\/runs\/\d+/.test(p), title: 'Run Detail' },
    { test: (p) => p.startsWith('/runs'), title: 'Disbursement Runs' },
    { test: (p) => p.startsWith('/dashboard'), title: 'Dashboard' },
  ];
  for (const rule of rules) {
    if (rule.test(relative)) return rule.title;
  }
  return 'Client Portal';
}

export const ClientPortalLayout: React.FC = () => {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { user, logout, isMirror, apiBase, authHeaders, cpPath, exitPath } = useCpPortal();
  const navigate = useNavigate();
  const location = useLocation();

  const navBase = isMirror && user ? cpPath('') : '/client';
  const pageTitle = useMemo(() => getPageTitle(location.pathname, navBase), [location.pathname, navBase]);

  const [prefunding, setPrefunding] = useState<{
    depositReference: string | null;
    availableCents: number;
    trafficLight: string;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/prefunding`, { headers: authHeaders() });
        const data = await res.json();
        if (!cancelled && data.success) {
          setPrefunding({
            depositReference: data.data.depositReference,
            availableCents: data.data.availableCents || 0,
            trafficLight: data.data.trafficLight || 'green',
          });
        }
      } catch {
        // Non-fatal — header just hides the pill.
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase, authHeaders]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const trafficColor =
    prefunding?.trafficLight === 'red' ? 'bg-red-500'
    : prefunding?.trafficLight === 'orange' ? 'bg-amber-400'
    : 'bg-emerald-500';
  const formatCents = (c: number) =>
    new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(c / 100);

  const displayName = user?.name || user?.email || 'User';
  const companyName = user?.companyName || 'Client Portal';
  const displayRole = user?.role || 'viewer';
  const visibleNavItems = useMemo(
    () => NAV_ITEMS.filter((item) => {
      if (item.adminOnly && user?.role !== 'admin') return false;
      if (item.makerOrAdminOnly && !['maker', 'admin'].includes(user?.role || '')) return false;
      if (item.mirrorHidden && isMirror) return false;
      if (item.revenueShareOnly && !user?.revenueShareEnabled) return false;
      if (item.passthroughOnly && !user?.passthroughEnabled) return false;
      return true;
    }),
    [user?.role, user?.revenueShareEnabled, user?.passthroughEnabled, isMirror],
  );
  const navGroups = useMemo(() => {
    const groups: { title: string; items: NavItem[] }[] = [];
    for (const item of visibleNavItems) {
      const last = groups[groups.length - 1];
      if (last && last.title === item.group) last.items.push(item);
      else groups.push({ title: item.group, items: [item] });
    }
    return groups;
  }, [visibleNavItems]);
  const mobileItems = visibleNavItems.filter((item) => item.mobile);
  const initialLetter = (user?.name?.charAt(0) || user?.email?.charAt(0) || 'U').toUpperCase();

  const handleExit = () => {
    setUserMenuOpen(false);
    setMobileNavOpen(false);
    if (isMirror) {
      navigate(exitPath, { replace: true });
      return;
    }
    logout?.();
    navigate('/client/login', { replace: true });
  };

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
      setUserMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen, handleClickOutside]);

  const renderNavLink = (item: NavItem, opts?: { collapsed?: boolean; onNavigate?: () => void }) => {
    const Icon = item.icon;
    const to = cpPath(item.suffix);
    const isCollapsed = !!opts?.collapsed;
    return (
      <NavLink
        key={item.suffix}
        to={to}
        end={item.suffix === '/dashboard'}
        title={isCollapsed ? item.label : undefined}
        aria-label={isCollapsed ? item.label : undefined}
        onClick={opts?.onNavigate}
        className={({ isActive }) =>
          [
            'relative flex min-h-[44px] items-center gap-3 rounded-lg px-2.5 py-2 text-sm transition-colors',
            isCollapsed ? 'justify-center' : '',
            isActive
              ? 'bg-[var(--sidebar-accent)] font-medium text-white'
              : 'text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-accent)]',
          ].join(' ')
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <span
                className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r bg-[var(--sidebar-primary)]"
                aria-hidden
              />
            )}
            <Icon className="h-[18px] w-[18px] flex-shrink-0" aria-hidden />
            <span
              className={`truncate transition-opacity duration-200 ${
                isCollapsed ? 'pointer-events-none w-0 opacity-0' : 'opacity-100'
              }`}
            >
              {item.label}
            </span>
          </>
        )}
      </NavLink>
    );
  };

  const sidebar = (opts?: { collapsed?: boolean; onNavigate?: () => void }) => (
    <>
      <div
        className={`flex min-h-[56px] items-center gap-3 border-b border-[var(--sidebar-border)] py-3 ${
          opts?.collapsed ? 'justify-center px-2' : 'px-4'
        }`}
      >
        <img src={logoIcon} alt="MyMoolah" className="h-9 w-9 flex-shrink-0 rounded-lg" />
        <div
          className={`min-w-0 flex-1 overflow-hidden transition-opacity duration-200 ${
            opts?.collapsed ? 'pointer-events-none opacity-0' : 'opacity-100'
          }`}
        >
          <p className="truncate text-[10px] font-medium uppercase tracking-wide text-[var(--sidebar-foreground)]">
            MyMoolah
          </p>
          <div className="mt-0.5">
            <ClientPartnerBrandBar
              branding={user?.branding}
              companyName={companyName}
              compact
              tone="dark"
            />
          </div>
        </div>
      </div>

      <nav className="flex-1 space-y-5 overflow-y-auto px-2 py-4">
        {navGroups.map((group) => (
          <div key={group.title}>
            <p
              className={`mb-2 px-2 text-[10px] font-medium uppercase tracking-widest text-[var(--muted-foreground)] ${
                opts?.collapsed ? 'pointer-events-none h-0 overflow-hidden opacity-0' : 'opacity-100'
              }`}
            >
              {group.title}
            </p>
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.suffix}>{renderNavLink(item, opts)}</li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </>
  );

  return (
    <div className="flex h-full min-h-0 flex-1 overflow-hidden bg-[var(--background)]">
      <aside
        className={`hidden h-full flex-shrink-0 flex-col overflow-hidden border-r border-[var(--sidebar-border)] bg-[var(--sidebar)] transition-[width] duration-200 ease-in-out md:flex ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        {sidebar({ collapsed })}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="flex min-h-[44px] w-full items-center justify-center border-t border-[var(--sidebar-border)] text-[var(--sidebar-foreground)] transition-colors hover:bg-[var(--sidebar-accent)] hover:text-white"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4 flex-shrink-0" aria-hidden />
          ) : (
            <ChevronLeft className="h-4 w-4 flex-shrink-0" aria-hidden />
          )}
        </button>
      </aside>

      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close navigation"
            onClick={() => setMobileNavOpen(false)}
          />
          <aside className="relative z-10 flex h-full w-64 flex-col bg-[var(--sidebar)] shadow-xl">
            <div className="flex items-center justify-between border-b border-[var(--sidebar-border)] px-3 py-2">
              <span className="text-xs font-medium text-[var(--sidebar-foreground)]">Menu</span>
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-lg text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-accent)]"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
            {sidebar({ onNavigate: () => setMobileNavOpen(false) })}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-14 flex-shrink-0 items-center justify-between gap-3 border-b border-[var(--border)] bg-white px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              className="flex h-11 w-11 items-center justify-center rounded-lg text-[var(--muted-foreground)] hover:bg-[var(--muted)] md:hidden"
              aria-label="Open navigation"
              onClick={() => setMobileNavOpen(true)}
            >
              <Menu className="h-5 w-5" aria-hidden />
            </button>
            <h1 className="truncate text-base font-semibold text-[var(--foreground)]">{pageTitle}</h1>
          </div>

          <div className="flex flex-shrink-0 items-center gap-2">
            {prefunding?.depositReference && (
              <div
                className="hidden items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-1.5 sm:flex"
                title={`Deposit reference: ${prefunding.depositReference}\nAvailable float: ${formatCents(prefunding.availableCents)}`}
              >
                <Wallet className="h-3.5 w-3.5 text-[var(--muted-foreground)]" aria-hidden />
                <span className="font-mono text-xs font-medium text-[var(--foreground)]">
                  {prefunding.depositReference}
                </span>
                <span className={`h-2 w-2 rounded-full ${trafficColor}`} aria-hidden />
                <span className="font-ledger text-xs text-[var(--muted-foreground)]">
                  {formatCents(prefunding.availableCents)}
                </span>
              </div>
            )}

            <span className="hidden rounded-full border border-[var(--border)] bg-[var(--muted)]/50 px-2.5 py-1 text-[11px] font-medium capitalize text-[var(--muted-foreground)] sm:inline-flex">
              {displayRole}
            </span>

            <div ref={userMenuRef} className="relative">
              <button
                type="button"
                onClick={() => setUserMenuOpen((o) => !o)}
                aria-expanded={userMenuOpen}
                aria-haspopup="true"
                className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--card)] py-1 pl-1 pr-2 shadow-sm transition-colors hover:bg-[var(--muted)]"
              >
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[var(--primary)] text-xs font-semibold text-[var(--primary-foreground)]">
                  {initialLetter}
                </div>
                <span className="hidden max-w-[120px] truncate text-xs font-medium text-[var(--foreground)] lg:block">
                  {displayName}
                </span>
                <ChevronDown
                  className={`h-4 w-4 flex-shrink-0 text-[var(--muted-foreground)] transition-transform duration-200 ${
                    userMenuOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden
                />
              </button>

              {userMenuOpen && (
                <div
                  className="absolute right-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--card)] py-1 shadow-lg"
                  role="menu"
                >
                  <div className="border-b border-[var(--border)] px-4 py-3">
                    <p className="truncate text-sm font-medium text-[var(--foreground)]">{displayName}</p>
                    <p className="truncate text-xs text-[var(--muted-foreground)]">{user?.email}</p>
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-[var(--muted-foreground)]">
                      <Building2 className="h-3 w-3" aria-hidden />
                      {companyName}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleExit}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-red-600 transition-colors hover:bg-red-50"
                  >
                    <LogOut className="h-4 w-4" aria-hidden />
                    {isMirror ? 'Exit admin mirror' : 'Sign out'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 pb-20 sm:p-6 md:pb-6">
          <Outlet />
        </main>
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-around border-t border-[var(--border)] bg-white py-2 md:hidden">
        {mobileItems.map((item) => {
          const Icon = item.icon;
          const to = cpPath(item.suffix);
          return (
            <NavLink
              key={item.suffix}
              to={to}
              end={item.suffix === '/dashboard'}
              className={({ isActive }) =>
                [
                  'flex flex-col items-center gap-0.5 px-3 py-1 text-[10px]',
                  isActive
                    ? 'font-medium text-[var(--primary)]'
                    : 'text-[var(--muted-foreground)]',
                ].join(' ')
              }
            >
              <Icon className="h-5 w-5" aria-hidden />
              <span>{item.label === 'Upload Beneficiaries' ? 'Upload' : item.label === 'Disbursement Runs' ? 'Runs' : item.label === 'Registered users' ? 'Users' : item.label}</span>
            </NavLink>
          );
        })}
        <button
          type="button"
          onClick={handleExit}
          className="flex flex-col items-center gap-0.5 px-3 py-1 text-[10px] text-[var(--muted-foreground)]"
        >
          <LogOut className="h-5 w-5" aria-hidden />
          <span>{isMirror ? 'Exit' : 'Sign out'}</span>
        </button>
      </nav>
    </div>
  );
};
